import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import {
  Wrapper,
  Container,
  InnerContainer,
  P2,
  P3,
  H2,
} from "@components/styled"
import Slider from "react-slick"
import { media } from "@styles/theme"

import NextArrow from "@assets/Next.svg"
import NextDisabled from "@assets/NextDisabled.svg"
import PrevArrow from "@assets/Prev.svg"
import PrevDisabled from "@assets/PrevDisabled.svg"
import getId from "@helpers/GetId"

const StyledWrapper = styled(Wrapper as any)`
  padding-bottom: ${({ theme }) => theme.sectionPadding.lg};
  
  @media ${media.s} {
    padding-bottom: ${({ theme }) => theme.sectionPadding.s};
    /* padding-bottom: 200px; */
  }

  ${Container} {
    padding-bottom: 100px;
  }
`

const BorderTop = styled.div`
  width: 64%;
  margin: 0 auto;
  border-top: 1px solid ${({ theme }) => theme.colors.grey};
  margin-bottom: 40px;

  @media ${media.sm} {
    width: 76%;
  }

  @media ${media.s} {
    width: 100%;
  }
`

const SliderWrapper = styled.div`
  position: relative;
  
  &:before {
    content: "";
    position: absolute;
    height: 100%;
    width: 18%;
    left: 0;
    top: 0;
    background: linear-gradient(90deg, rgba(247, 249, 242,1) 0%, rgba(247, 249, 242,1) 80%, rgba(247, 249, 242,0) 100%);
    z-index: 1;

    @media ${media.sm} {
      display: none;
    }
  }

  &:after {
    content: "";
    position: absolute;
    height: 100%;
    width: 18%;
    right: 0;
    top: 0;
    background: linear-gradient(90deg, rgba(247, 249, 242,0) 0%, rgba(247, 249, 242,1) 20%, rgba(247, 249, 242,1) 100%);
    z-index: 1;

    @media ${media.sm} {
      display: none;
    }
  }

  .slick-slider {
    width: 100%;
  }

  .slick-slide {
    height: 100%;

    & > div > div {
      height: 100%;
    }

    & > div {
      width: 100%;
    }
  }

  .slick-arrow {
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
    height: 50px;
    width: 50px;
    z-index: 1;
    left: unset;
    top: unset;
    right: 0;
    bottom: 0;
    transition: opacity 0.3s;
    background-color: #F7F9F2;
    border-radius: 50%;

    &:hover {
      background: #F7F9F2;

      &:before {
        opacity: 0.5;
        @media ${media.sm} {
          opacity: 1;
        }
      }
    }

    &:before {
      opacity: 1;
      content: "";
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
    }

    &.slick-prev {
      &:before {
        background: url(${PrevArrow});
      }
      right: calc(18% + 60px);

      @media ${media.sm} {
        right: calc(12% + 60px);
      }

      @media (max-width: 576px) {
        right: unset;
        left: 12%;
        top: unset;
        bottom: -128px;
      }

      @media ${media.s} {
        left: 0;
      }


      &.slick-disabled {
        &:before {
          opacity: 1;
          background: url(${PrevDisabled});
        }
      }
    }

    &.slick-next {
      &:before {
        background: url(${NextArrow});
      }
      right: 18%;

      @media ${media.sm} {
        right: 12%;
      }

      @media (max-width: 576px) {
        right: unset;
        left: calc(12% + 60px);
        bottom: -128px;
      }

      @media ${media.s} {
        left: 60px;
      }

      &.slick-disabled {
        &:before {
          opacity: 1;
          background: url(${NextDisabled});
        }
      }
    }

    &.slick-disabled {
    }
  }
`

const Testimonial = styled.div`
  color: ${({ theme }) => theme.colors.dark};
  /* max-width: 610px; */
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-right: 40px;
  padding-left: 30px;
  position: relative;
  width: 65%;

  @media ${media.lg} {
    width: 80%;
  }

  @media ${media.md} {
    width: 100%;
  }


  @media ${media.sm} {
    padding-right: 20px;
  }



  @media ${media.s} {
    padding: 39px 0 0 0;
  }

  &:before {
    content: "“";
    position: absolute;
    left: 0;
    top: 6px;
    font-size: 30px;

    @media ${media.s} {
      top: 12px;
    }
  }

  &.no-padding {
    @media ${media.sm} {
      padding: 0;
    }
  }

  // scroll behavior params:
  scroll-snap-align: center;

  ${H2} {
    margin-top: 0;
    color: ${({ theme }) => theme.colors.dark};
  }
`

const CreditInfo = styled.div`
    display: flex;
    flex-direction: column;

    ${P2} {
      margin: 0;
    }
`;

const IconsBlock = styled.div`
  display: flex;
  margin-top: 10px;
`;

const StateBlock = styled.div`
  display: flex;
  align-items: center;
  border-right: 1px solid #C2C2C2;
  margin-right: 10px;
  padding-right: 10px;

  & > div {
    margin-right: 6px;
  }
`;

const EmployeeCountBlock = styled.div`
  display: flex;
  align-items: center;

  & > div {
    margin-right: 6px;
  }
`;

const TestimonialWrapper = styled.div`
  & > div {
    position: relative;
    width: 64%;
    margin: 0 auto;

    @media ${media.sm} {
      width: 76%;
    }

    @media ${media.s} {
      width: 100%;
    }
  }
`

const settings = {
  dots: false,
  infinite: false,
  speed: 500,
  draggable: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
}

const SingleTestimonialSlider: React.FC<{ slice: any }> = ({ slice }) => {
  const navigationLabel = slice.primary?.navigation_label
  const items = slice.items

  return (
    <StyledWrapper
      id={navigationLabel && getId(navigationLabel, slice.slice_type)}
    >
      <Container>
        <InnerContainer>
          <BorderTop />
          <SliderWrapper>
            <Slider {...settings}>
              {items.map((testimonial: any, index: number) => (
                <TestimonialWrapper
                  key={`single_testimonial_${testimonial.employee.text}_${index}`}
                >
                  <div>
                    <Testimonial>
                      <H2>{testimonial.feedback.text}</H2>
                      <CreditInfo>
                        <P2>{testimonial.position.text}</P2> {/* testimonial.role.text */}
                        <IconsBlock>
                          <StateBlock>
                            <div title="State" >
                              <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.1 2.20834L11.1 0.541672H11.0417C11.0029 0.537773 10.9638 0.537773 10.925 0.541672H10.7333H10.625H10.5667L5.83334 2.16667L1.1 0.541672C0.974682 0.500348 0.841339 0.489376 0.710949 0.509659C0.580559 0.529942 0.45685 0.5809 0.350003 0.658338C0.242301 0.735019 0.15439 0.836224 0.0935318 0.953594C0.0326733 1.07096 0.000614128 1.20113 2.77795e-06 1.33334V13C-0.00044819 13.1747 0.0540186 13.3451 0.155704 13.4872C0.25739 13.6293 0.401152 13.7358 0.56667 13.7917L5.56667 15.4583C5.73454 15.5131 5.91546 15.5131 6.08334 15.4583L10.8333 13.875L15.5667 15.5C15.6552 15.512 15.7449 15.512 15.8333 15.5C16.0076 15.5025 16.1777 15.4467 16.3167 15.3417C16.4244 15.265 16.5123 15.1638 16.5731 15.0464C16.634 14.929 16.6661 14.7989 16.6667 14.6667V3C16.6671 2.8253 16.6127 2.65488 16.511 2.51281C16.4093 2.37075 16.2655 2.26424 16.1 2.20834ZM5 13.5083L1.66667 12.4V2.49167L5 3.6V13.5083ZM10 12.4L6.66667 13.5083V3.6L10 2.49167V12.4ZM15 13.5083L11.6667 12.4V2.49167L15 3.6V13.5083Z" fill="#707070"/>
                              </svg>
                            </div>
                            <span>{testimonial.state.text}</span>
                          </StateBlock>
                          <EmployeeCountBlock>
                            <div title="Number of employees">
                              <svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9.75016 8.18336C10.1948 7.79847 10.5515 7.32243 10.7959 6.78754C11.0403 6.25266 11.1668 5.67145 11.1668 5.08335C11.1668 3.97829 10.7278 2.91848 9.94644 2.13708C9.16504 1.35567 8.10523 0.916687 7.00016 0.916687C5.89509 0.916687 4.83529 1.35567 4.05388 2.13708C3.27248 2.91848 2.8335 3.97829 2.8335 5.08335C2.83349 5.67145 2.96 6.25266 3.20443 6.78754C3.44887 7.32243 3.80552 7.79847 4.25016 8.18336C3.08361 8.71159 2.09388 9.56463 1.39931 10.6405C0.704742 11.7163 0.334723 12.9694 0.333496 14.25C0.333496 14.471 0.421293 14.683 0.577574 14.8393C0.733854 14.9956 0.945816 15.0834 1.16683 15.0834C1.38784 15.0834 1.5998 14.9956 1.75609 14.8393C1.91237 14.683 2.00016 14.471 2.00016 14.25C2.00016 12.9239 2.52695 11.6522 3.46463 10.7145C4.40231 9.77681 5.67408 9.25002 7.00016 9.25002C8.32625 9.25002 9.59802 9.77681 10.5357 10.7145C11.4734 11.6522 12.0002 12.9239 12.0002 14.25C12.0002 14.471 12.088 14.683 12.2442 14.8393C12.4005 14.9956 12.6125 15.0834 12.8335 15.0834C13.0545 15.0834 13.2665 14.9956 13.4228 14.8393C13.579 14.683 13.6668 14.471 13.6668 14.25C13.6656 12.9694 13.2956 11.7163 12.601 10.6405C11.9064 9.56463 10.9167 8.71159 9.75016 8.18336ZM7.00016 7.58335C6.50571 7.58335 6.02236 7.43673 5.61124 7.16203C5.20011 6.88733 4.87968 6.49688 4.69046 6.04006C4.50125 5.58325 4.45174 5.08058 4.5482 4.59563C4.64466 4.11068 4.88277 3.66522 5.2324 3.31559C5.58203 2.96596 6.02749 2.72785 6.51244 2.63139C6.99739 2.53493 7.50006 2.58444 7.95687 2.77366C8.41369 2.96287 8.80413 3.28331 9.07884 3.69443C9.35354 4.10555 9.50016 4.5889 9.50016 5.08335C9.50016 5.7464 9.23677 6.38228 8.76793 6.85112C8.29909 7.31996 7.6632 7.58335 7.00016 7.58335ZM15.1168 7.85002C15.6501 7.24946 15.9985 6.50757 16.12 5.71364C16.2415 4.91971 16.1309 4.10758 15.8016 3.37502C15.4723 2.64246 14.9383 2.02068 14.2639 1.58454C13.5895 1.1484 12.8033 0.916472 12.0002 0.916687C11.7792 0.916687 11.5672 1.00448 11.4109 1.16076C11.2546 1.31705 11.1668 1.52901 11.1668 1.75002C11.1668 1.97103 11.2546 2.183 11.4109 2.33928C11.5672 2.49556 11.7792 2.58335 12.0002 2.58335C12.6632 2.58335 13.2991 2.84675 13.7679 3.31559C14.2368 3.78443 14.5002 4.42031 14.5002 5.08335C14.499 5.52105 14.3829 5.95077 14.1635 6.32954C13.9442 6.7083 13.6292 7.02284 13.2502 7.24169C13.1266 7.31296 13.0234 7.41474 12.9505 7.53731C12.8775 7.65987 12.8372 7.79911 12.8335 7.94169C12.83 8.08316 12.8626 8.22319 12.9282 8.34857C12.9938 8.47395 13.0903 8.58057 13.2085 8.65836L13.5335 8.87502L13.6418 8.93336C14.6463 9.40979 15.4938 10.1633 16.0843 11.1053C16.6749 12.0472 16.984 13.1383 16.9752 14.25C16.9752 14.471 17.063 14.683 17.2192 14.8393C17.3755 14.9956 17.5875 15.0834 17.8085 15.0834C18.0295 15.0834 18.2415 14.9956 18.3978 14.8393C18.554 14.683 18.6418 14.471 18.6418 14.25C18.6486 12.9712 18.3284 11.7119 17.7114 10.5917C17.0944 9.47158 16.2013 8.52778 15.1168 7.85002Z" fill="#707070"/>
                              </svg>
                            </div>
                            <span>{testimonial.employee.text}</span>
                          </EmployeeCountBlock>
                        </IconsBlock>
                      </CreditInfo>
                    </Testimonial>
                  </div>
                </TestimonialWrapper>
              ))}
            </Slider>
          </SliderWrapper>
        </InnerContainer>
      </Container>
    </StyledWrapper>
  )
}

export default SingleTestimonialSlider

export const pageQuery = graphql`
  fragment ServicesTestimonialSliderWithIconsFragment on PrismicServicesDataBodySingleTestimonialWithIcons {
    items {
      feedback {
        text
      }
      employee {
        text
      }
      position {
        text
      }
      state {
        text
      }
    }
  }

`
